import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AppLayout } from '../../components/layout';
import { Button, Input, Textarea, Select, Alert, CopyButton } from '../../components/core';
import { http } from '../../lib/api';
import { loadCheckout } from '../../lib/razorpay';
import { useUiStore } from '../../store/ui';
import type { BillingCycle, PlanDto, RazorpayOrderResponseDto, StreamDto } from '@livestreamx/shared-types';
import { priceForCycle } from '@livestreamx/shared-types';
import { NAV } from './nav';

interface ActivePlan { planId: string; name: string; endAt: string | null; maxStreams: number; remainingStreams: number; }

export function CreateStreamPage() {
  const ui = useUiStore();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [recording, setRecording] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<StreamDto | null>(null);
  // null while loading the tenant's entitlement; [] means no plan -> subscribe first.
  const [activePlans, setActivePlans] = useState<ActivePlan[] | null>(null);
  const [catalog, setCatalog] = useState<PlanDto[]>([]);
  const [planId, setPlanId] = useState('');
  const [paying, setPaying] = useState<string | null>(null);
  const [payCycle, setPayCycle] = useState<Record<string, BillingCycle>>({});

  const loadEntitlement = () =>
    http
      .get<{ plans?: ActivePlan[] } | null>('/api/billing/subscription')
      .then((s) => {
        // A plan with no free stream slot is already spoken for — offering it
        // again just leads to a "Stream limit reached" error after the fact.
        const list = (s?.plans ?? []).filter((p) => p.remainingStreams > 0);
        setActivePlans(list);
        setPlanId((prev) => (prev && list.some((p) => p.planId === prev) ? prev : list[0]?.planId ?? ''));
      })
      .catch(() => setActivePlans([]));

  useEffect(() => {
    void http.get<PlanDto[]>('/api/plans').then(setCatalog).catch(() => {});
    void loadEntitlement();
  }, []);

  const subscribe = async (plan: PlanDto) => {
    const cycle = payCycle[plan.id] ?? 'MONTHLY';
    setPaying(plan.id);
    try {
      const order = await http.post<RazorpayOrderResponseDto>('/api/billing/orders', { planId: plan.id, billingCycle: cycle });
      const Razorpay = await loadCheckout();
      new Razorpay({
        key: order.keyId,
        amount: order.amount,
        currency: order.currency,
        name: 'LiveStreamX',
        description: plan.name,
        order_id: order.orderId,
        handler: (res) => {
          ui.toast('success', 'Payment submitted', `Ref ${res.razorpay_payment_id} — activating (server verifies via webhook)`);
          setPaying(null);
          setTimeout(() => void loadEntitlement(), 6000);
        },
        modal: { ondismiss: () => setPaying(null) },
      }).open();
    } catch (err) {
      const msg = (err as Error).message ?? '';
      ui.toast('danger', msg.includes('not configured') ? 'Billing not configured' : 'Payment error', msg);
      setPaying(null);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const data = await http.post<StreamDto>('/api/tenant/streams', {
        name,
        description,
        recordingEnabled: recording,
        planId: planId || undefined,
      });
      setResult(data);
      ui.toast('success', 'Stream created!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create stream');
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <AppLayout brand="LiveStreamX" navGroups={NAV}>
        <div className="create-stream-success">
          <h1 className="page-title">Stream created!</h1>
          <p className="text-muted mb-4">Your stream is ready. Configure your encoder with the details below, then start broadcasting.</p>
          <div className="card">
            <div className="field"><label>Stream name</label><div className="font-medium">{result.name}</div></div>
            <div className="field"><label>RTMP server (base URL)</label><code>{result.rtmpUrl}</code></div>
            <div className="field"><label>Stream key</label><div className="flex items-center gap-2"><code>{result.streamKey}</code><CopyButton text={result.streamKey} /></div></div>
            <div className="field"><label>Full publish URL (server + key)</label><div className="flex items-center gap-2"><code style={{ overflowWrap: 'anywhere' }}>{result.rtmpUrl}/{result.streamKey}</code><CopyButton text={`${result.rtmpUrl}/${result.streamKey}`} /></div></div>
            <div className="field"><label>HLS playback</label><code>{result.hlsUrl}</code></div>
            <div className="field"><label>Player URL</label><div className="flex items-center gap-2"><code>{result.playerUrl}</code><CopyButton text={result.playerUrl} /></div></div>
          </div>
          <div className="mt-3 flex gap-2">
            <Link to={`/streams/${result.slug}`}><Button>View stream</Button></Link>
            <Link to="/streams"><Button variant="secondary">All streams</Button></Link>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout brand="LiveStreamX" navGroups={NAV}>
      <div className="create-stream-page">
        <div className="flex items-center justify-between mb-2">
          <h1 className="page-title">Create stream</h1>
          <Link to="/streams"><Button variant="secondary" size="sm">← Back</Button></Link>
        </div>
        <p className="text-muted mb-4">Create a new live stream with its own RTMP input URL and stream key.</p>
        {activePlans !== null && activePlans.length === 0 ? (
          <div className="card">
            <div className="field">
              <label>No active plan</label>
              <p className="text-sm text-muted" style={{ margin: 0 }}>
                Pick a plan and complete payment — this stream is created as soon as the payment is confirmed.
              </p>
            </div>
            {catalog.length === 0 ? <div className="text-muted">Loading plans…</div> : null}
            {catalog.map((p) => {
              const cycle = payCycle[p.id] ?? 'MONTHLY';
              const amount = priceForCycle(p, cycle);
              return (
                <div key={p.id} className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">{p.name}</div>
                    {p.description ? <div className="text-sm text-muted">{p.description}</div> : null}
                    <div className="plan-limits">
                      <div className="plan-limit"><span className="plan-limit-label">Streams</span><span className="plan-limit-value">{p.maxStreams}</span></div>
                      <div className="plan-limit"><span className="plan-limit-label">Dest/Stream</span><span className="plan-limit-value">{p.maxDestinationsPerStream || '∞'}</span></div>
                      <div className="plan-limit"><span className="plan-limit-label">Total Dest</span><span className="plan-limit-value">{p.maxTotalDestinations || '∞'}</span></div>
                      <div className="plan-limit"><span className="plan-limit-label">Storage</span><span className="plan-limit-value">{p.maxStorageGb} GB</span></div>
                      <div className="plan-limit"><span className="plan-limit-label">Bandwidth</span><span className="plan-limit-value">{p.maxBandwidthGb} GB</span></div>
                      <div className="plan-limit"><span className="plan-limit-label">In bitrate</span><span className="plan-limit-value">{p.maxInboundBitrateMbps || '∞'} Mbps</span></div>
                      <div className="plan-limit"><span className="plan-limit-label">Out bitrate</span><span className="plan-limit-value">{p.maxOutboundBitrateMbps || '∞'} Mbps</span></div>
                      <div className="plan-limit"><span className="plan-limit-label">Recording</span><span className="plan-limit-value">{p.recordingEnabled ? '✓' : '✗'}</span></div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => {
                      const opts: BillingCycle[] = p.priceDay != null
                        ? ['DAILY', 'MONTHLY', 'YEARLY']
                        : ['MONTHLY', 'YEARLY'];
                      const next = opts[(opts.indexOf(cycle) + 1) % opts.length];
                      setPayCycle((c) => ({ ...c, [p.id]: next }));
                    }}>
                      {cycle === 'MONTHLY' ? 'Monthly' : cycle === 'YEARLY' ? 'Yearly' : 'Daily'}
                    </Button>
                    <Button variant="secondary" disabled={paying !== null} onClick={() => void subscribe(p)}>
                      {paying === p.id ? 'Opening checkout…' : `Pay ₹${amount.toLocaleString('en-IN')}`}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
        {error && <Alert kind="danger">{error}</Alert>}
        <form onSubmit={submit} className="stream-form" hidden={activePlans !== null && activePlans.length === 0}>
          {activePlans !== null && activePlans.length > 1 ? (
            <Select
              label="Plan for this stream"
              value={planId}
              options={activePlans.map((p) => ({ value: p.planId, label: p.maxStreams > 1 ? `${p.name} (${p.remainingStreams} of ${p.maxStreams} free)` : p.name }))}
              onChange={setPlanId}
              hint="The stream is billed against this plan."
            />
          ) : null}
          <Input label="Stream name" value={name} onChange={setName} placeholder="e.g. Weekly Q&A Show" required autoComplete="off" hint="A friendly name for your stream" />
          <Textarea label="Description (optional)" value={description} onChange={setDescription} placeholder="Describe this stream for your audience" rows={3} autoComplete="off" />
          <Select
            label="Recording"
            value={recording ? 'yes' : 'no'}
            options={[{ value: 'no', label: 'No recording' }, { value: 'yes', label: 'Record to storage' }]}
            onChange={(v) => setRecording(v === 'yes')}
          />
          <div className="flex justify-end gap-2 mt-4">
            <Link to="/streams"><Button variant="secondary" disabled={loading}>Cancel</Button></Link>
            <Button type="submit" variant="primary" disabled={loading || !name.trim()}>{loading ? 'Creating…' : 'Create stream'}</Button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}
